// Enterprise Authentication Service
import { UserRole } from '../types';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  department: string;
  permissions: string[];
  sessionId: string;
  lastActivity: Date;
  profilePicture?: string;
  tenantId: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;
  sessionExpiry: Date | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface OAuthProvider {
  type: 'microsoft' | 'google' | 'okta' | 'saml';
  clientId: string;
  redirectUri: string;
}

class AuthService {
  private readonly SESSION_KEY = 'hr_analytics_session';
  private readonly REFRESH_TOKEN_KEY = 'hr_analytics_refresh_token';
  private readonly SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  private sessionTimer: number | null = null;

  // Mock authentication for demo - replace with real auth service
  async login(credentials: LoginCredentials): Promise<AuthUser> {
    try {
      // Simulate API call
      await this.delay(1000);
      
      // Mock user lookup
      const mockUser = this.getMockUser(credentials.email);
      
      if (!mockUser) {
        throw new Error('Invalid credentials');
      }

      // Generate session
      const sessionId = this.generateSessionId();
      const user: AuthUser = {
        id: mockUser.id!,
        email: mockUser.email!,
        name: mockUser.name!,
        role: mockUser.role!,
        department: mockUser.department!,
        permissions: mockUser.permissions!,
        tenantId: mockUser.tenantId!,
        sessionId,
        lastActivity: new Date(),
      };

      // Store session
      this.storeSession(user, credentials.rememberMe);
      this.startSessionTimer();

      return user;
    } catch (error) {
      throw new Error('Authentication failed');
    }
  }

  async loginWithOAuth(provider: OAuthProvider): Promise<AuthUser> {
    try {
      // Simulate OAuth flow
      const authUrl = this.buildOAuthUrl(provider);
      
      // In real implementation, redirect to OAuth provider
      console.log('Redirect to:', authUrl);
      
      // Mock successful OAuth response
      await this.delay(2000);
      
      const mockUser: AuthUser = {
        id: 'oauth_user_1',
        email: 'user@cloudonmars.com',
        name: 'OAuth User',
        role: 'HR_LEADER',
        department: 'Human Resources',
        permissions: ['canViewAllEmployees', 'canViewSensitiveMetrics', 'canExportData'],
        sessionId: this.generateSessionId(),
        lastActivity: new Date(),
        tenantId: 'cloudonmars.onmicrosoft.com'
      };

      this.storeSession(mockUser, true);
      this.startSessionTimer();

      return mockUser;
    } catch (error) {
      throw new Error('OAuth authentication failed');
    }
  }

  async logout(): Promise<void> {
    try {
      // Clear session timer
      if (this.sessionTimer) {
        clearTimeout(this.sessionTimer);
        this.sessionTimer = null;
      }

      // Clear stored session
      localStorage.removeItem(this.SESSION_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(this.SESSION_KEY);

      // In real implementation, call server logout endpoint
      await this.delay(500);
    } catch (error) {
      console.error('Logout error:', error);
      // Always clear local storage even if server call fails
      localStorage.removeItem(this.SESSION_KEY);
      sessionStorage.removeItem(this.SESSION_KEY);
    }
  }

  async refreshToken(): Promise<AuthUser | null> {
    try {
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        return null;
      }

      // Simulate token refresh
      await this.delay(1000);
      
      const currentUser = this.getCurrentUser();
      if (!currentUser) {
        return null;
      }

      // Update session with new timestamp
      const updatedUser: AuthUser = {
        ...currentUser,
        lastActivity: new Date(),
        sessionId: this.generateSessionId(),
      };

      this.storeSession(updatedUser, true);
      this.startSessionTimer();

      return updatedUser;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }

  getCurrentUser(): AuthUser | null {
    try {
      const sessionData = localStorage.getItem(this.SESSION_KEY) || 
                         sessionStorage.getItem(this.SESSION_KEY);
      
      if (!sessionData) {
        return null;
      }

      const user: AuthUser = JSON.parse(sessionData);
      
      // Check session expiry
      if (this.isSessionExpired(user)) {
        this.logout();
        return null;
      }

      return user;
    } catch (error) {
      console.error('Error retrieving current user:', error);
      return null;
    }
  }

  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  }

  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    return user?.permissions.includes(permission) ?? false;
  }

  hasRole(role: UserRole): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  // Session management
  updateLastActivity(): void {
    const user = this.getCurrentUser();
    if (user) {
      user.lastActivity = new Date();
      this.storeSession(user, true);
    }
  }

  private startSessionTimer(): void {
    if (this.sessionTimer) {
      window.clearTimeout(this.sessionTimer);
    }

    this.sessionTimer = window.setTimeout(() => {
      this.handleSessionTimeout();
    }, this.SESSION_TIMEOUT);
  }

  private async handleSessionTimeout(): Promise<void> {
    const user = this.getCurrentUser();
    if (!user) return;

    // Try to refresh token first
    const refreshedUser = await this.refreshToken();
    
    if (!refreshedUser) {
      // Session expired, logout user
      await this.logout();
      
      // Emit session timeout event
      window.dispatchEvent(new CustomEvent('sessionTimeout', {
        detail: { reason: 'timeout' }
      }));
    }
  }

  private isSessionExpired(user: AuthUser): boolean {
    const lastActivity = new Date(user.lastActivity);
    const now = new Date();
    const timeDiff = now.getTime() - lastActivity.getTime();
    
    return timeDiff > this.SESSION_TIMEOUT;
  }

  private storeSession(user: AuthUser, persistent: boolean = false): void {
    const storage = persistent ? localStorage : sessionStorage;
    storage.setItem(this.SESSION_KEY, JSON.stringify(user));
    
    if (persistent) {
      // Generate and store refresh token
      const refreshToken = this.generateRefreshToken();
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  private getMockUser(email: string): Partial<AuthUser> | null {
    // Mock user database - replace with real user lookup
    const mockUsers: Record<string, Partial<AuthUser>> = {
      'hr.leader@cloudonmars.com': {
        id: 'user_hr_001',
        email: 'hr.leader@cloudonmars.com',
        name: 'Dr. Sarah Müller',
        role: 'HR_LEADER',
        department: 'Human Resources',
        permissions: [
          'canViewAllEmployees',
          'canViewSensitiveMetrics',
          'canViewAIInsights',
          'canExportData',
          'canManageUsers',
          'canViewCompensationData',
          'canViewAttritionPredictions'
        ],
        tenantId: 'cloudonmars.onmicrosoft.com'
      },
      'manager@cloudonmars.com': {
        id: 'user_mgr_001',
        email: 'manager@cloudonmars.com',
        name: 'Michael Thompson',
        role: 'MANAGER',
        department: 'Clinical Operations',
        permissions: ['canViewTeamData', 'canViewBasicMetrics'],
        tenantId: 'cloudonmars.onmicrosoft.com'
      },
      'recruiter@cloudonmars.com': {
        id: 'user_rec_001',
        email: 'recruiter@cloudonmars.com',
        name: 'Maria Gonzalez',
        role: 'RECRUITER',
        department: 'Human Resources',
        permissions: [
          'canViewRecruitmentData',
          'canManageRequisitions',
          'canViewCompensationData',
          'canExportData'
        ],
        tenantId: 'cloudonmars.onmicrosoft.com'
      }
    };

    return mockUsers[email] || null;
  }

  private generateSessionId(): string {
    return 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private generateRefreshToken(): string {
    return 'ref_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private buildOAuthUrl(provider: OAuthProvider): string {
    const params = new URLSearchParams({
      client_id: provider.clientId,
      redirect_uri: provider.redirectUri,
      response_type: 'code',
      scope: 'openid profile email',
      state: this.generateSessionId()
    });

    const baseUrls = {
      microsoft: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      google: 'https://accounts.google.com/oauth2/auth',
      okta: 'https://dev-123456.okta.com/oauth2/v1/authorize',
      saml: '/auth/saml/login'
    };

    return `${baseUrls[provider.type]}?${params.toString()}`;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default new AuthService();